import { GridCellKind } from "@glideapps/glide-data-grid";
import toast from "react-hot-toast";
import { processDateJS } from "../../../utils/dateProcessor";
import { getPreviousAP } from "../../../utils/PeriodUtils";

export const defaultPageSize = 7;

export const isStartDateNotOneDayAfterEnd = (cap, allCaps) => {
  const prevCap = getPreviousAP(cap, allCaps);
  const isPrevMADElectionOn = prevCap?.apply_mean_accounting_date_election;
  const endDate = isPrevMADElectionOn
    ? prevCap?.actual_end_date
    : prevCap?.end_date;
  if (!cap || !prevCap) return false;

  const actualStartDate = cap?.actual_start_date;
  const isMADElectionOn = cap?.apply_mean_accounting_date_election;

  if (!actualStartDate && isMADElectionOn) return true;
  if (!actualStartDate) return false;

  const nextDay = processDateJS(endDate)?.add(1, "day");
  return !processDateJS(actualStartDate)?.isSame(nextDay);
};

export const isActualEndOutOfRange = (cap) => {
  const actualEndDate = cap?.actual_end_date;
  if (!actualEndDate) return;

  const isEndDatesDiffMoreThan4Days =
    getDaysDifference(actualEndDate, cap["end_date"]) > 4;
  return isEndDatesDiffMoreThan4Days;
};

export const getDaysDifference = (date1, date2) => {
  return Math.abs(processDateJS(date1)?.diff(processDateJS(date2), "day"));
};

export const isActualStartOutOfRange = (cap) => {
  const actualStartDate = cap?.actual_start_date;
  if (!actualStartDate) return false;

  const isStartDatesDiffMoreThan4Days =
    getDaysDifference(actualStartDate, cap["start_date"]) > 4;
  return isStartDatesDiffMoreThan4Days;
};

const isActualStartTooEarly = (cap) => {
  const actualStartDate = cap?.actual_start_date;
  const actualEndDate = cap?.actual_end_date;
  if (!actualStartDate || !actualEndDate) return false;

  const firstValidStartDate = processDateJS(actualEndDate)?.subtract(
    12,
    "month",
  );
  const isStartDateBeforeFirstValidDate =
    processDateJS(actualStartDate)?.isBefore(firstValidStartDate);

  return isStartDateBeforeFirstValidDate;
};

export const isActualStartDateEntryInvalid = (cap, allCaps) => {
  const isMADElectionOn = cap?.apply_mean_accounting_date_election;
  const actualStartDate = cap?.actual_start_date;

  const startNotAfterCapEndDate = isStartDateNotOneDayAfterEnd(cap, allCaps);
  const isMoreThanOneYearPeriod = isActualStartTooEarly(cap);
  const isActualStartDateOutOfRange = isActualStartOutOfRange(cap);

  return (
    (isMADElectionOn && !actualStartDate) ||
    startNotAfterCapEndDate ||
    isMoreThanOneYearPeriod ||
    isActualStartDateOutOfRange
  );
};

export const isActualEndDateEntryInvalid = (cap) => {
  const isMADElectionOn = cap?.apply_mean_accounting_date_election;
  const actualEndDate = cap?.actual_end_date;

  const isMoreThanOneYearPeriod = isActualStartTooEarly(cap);
  const isActualEndDateOutOfRange = isActualEndOutOfRange(cap);

  return (
    (isMADElectionOn && !actualEndDate) ||
    isMoreThanOneYearPeriod ||
    isActualEndDateOutOfRange
  );
};

export const ensureBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  if (typeof value === "string") {
    const lowercased = value?.toLowerCase();
    if (lowercased === "true" || lowercased === "1") {
      return true;
    }
    if (lowercased === "false" || lowercased === "0") {
      return false;
    }
  }

  return Boolean(value);
};

const prepopulateWithPoADateIfInRange = (poaDate, apDate) => {
  return getDaysDifference(poaDate, apDate) <= 4 ? poaDate : undefined;
};

export const isPeriodMoreThan12Months = ({ period_start, period_end }) => {
  return (
    processDateJS(period_end)?.diff(processDateJS(period_start), "month") > 12
  );
};

const getRelevantActualDate = (cap, dateType, periodDate, selectedPeriod) => {
  return (
    cap?.[`actual_${dateType}`] ??
    (isPeriodMoreThan12Months(selectedPeriod)
      ? undefined
      : prepopulateWithPoADateIfInRange(periodDate, cap[dateType]))
  );
};

export const populateMADElectionDatesOnEnable = (
  cap,
  metric,
  value,
  selectedPeriod,
) => {
  const { period_start, period_end } = selectedPeriod;
  if (
    metric.metric_id === "apply_mean_accounting_date_election" &&
    value === true
  ) {
    cap["actual_start_date"] = processDateJS(
      getRelevantActualDate(cap, "start_date", period_start, selectedPeriod),
    );
    cap["actual_end_date"] = processDateJS(
      getRelevantActualDate(cap, "end_date", period_end, selectedPeriod),
    );
  }
};

export const clearMADElectionDatesOnDisable = (cap, metric, value) => {
  if (
    metric.metric_id === "apply_mean_accounting_date_election" &&
    value === false
  ) {
    cap["actual_start_date"] = undefined;
    cap["actual_end_date"] = undefined;
  }
};

export const validateActualDatesForCap = (cap, allCaps) => {
  if (isActualStartTooEarly(cap)) {
    toast.error(
      "Actual AP start date must be no more than 12 months before the Actual Accounting Period end date.",
    );
  }

  if (isActualStartOutOfRange(cap)) {
    toast.error(
      "Actual AP start date must be within 4 days of the Accounting Period start date.",
    );
  }

  if (isActualEndOutOfRange(cap)) {
    toast.error(
      "Actual AP end date must be within 4 days of the Accounting Period end date.",
    );
  }

  if (isStartDateNotOneDayAfterEnd(cap, allCaps)) {
    toast.error(
      "The actual AP start date must be one day after the AP end date or the actual AP end date of the immediately prior AP.",
    );
  }
};

const cellTextStyle = {
  bold: { baseFontStyle: "600 15px" },
  bigBold: { baseFontStyle: "600 18px" },
};

export const calculateCell = (
  metric,
  cap,
  col,
  span,
  {
    company,
    isCapDatesMatchPoaDates,
    isActualStartDateEntryInvalid,
    isActualEndDateEntryInvalid,
    isCapUsedInPriorPoas,
    lastRefreshedFromOnesourceDates,
    changedMetricsForCap,
  } = {},
) => {
  switch (metric?.type) {
    case "blank":
      return {
        kind: GridCellKind?.Text,
        allowOverlay: false,
        data: " ",
        displayData: " ",
        span: [0, span],
        themeOverride: {
          ...cellTextStyle[metric?.style],
          bgCell: "#F4F3F7",
        },
      };
    case "header":
      return {
        kind: GridCellKind?.Text,
        allowOverlay: false,
        data: metric?.display_name,
        displayData: metric?.display_name,
        span: [0, span],
        themeOverride: {
          ...cellTextStyle[metric?.style],
          bgCell: "#F4F3F7",
        },
      };
    case "subheader":
    case "note":
      return {
        kind: GridCellKind?.Text,
        allowOverlay: false,
        data: metric?.display_name,
        displayData: metric?.display_name,
        span: [0, span],
        themeOverride: {
          ...cellTextStyle[metric?.style],
          ...(!metric?.editable
            ? { bgCell: "#ffd5b7" }
            : { bgCell: "#FFFFFF" }),
          bgCell: "#FFFFFF",
        },
      };
  }

  // Col 0 is the metric name
  if (col === 0) {
    return {
      kind: GridCellKind?.Text,
      data: metric?.display_name,
      allowOverlay: false,
      displayData: metric?.display_name + (metric?.tooltipMessage ? " 🛈" : ""),
      allowWrapping: true,
      themeOverride: {
        ...cellTextStyle[metric?.style],
        ...(!metric?.editable ? { bgCell: "#ffd5b7" } : { bgCell: "#FFFFFF" }),
        bgCell: "#ffd5b7",
      },
    };
  }

  let cellType = GridCellKind?.Text;
  let value = cap[metric?.metric_id];
  let displayValue = value ? String(value) : "";
  let editable = metric?.hasOwnProperty("editable") ? metric?.editable : true;

  if (metric?.metric_id === "onesource_last_refresh_date_time") {
    value = lastRefreshedFromOnesourceDates;
  }

  if (metric?.metric_id === "apply_mean_accounting_date_election") {
    editable = !isCapDatesMatchPoaDates;
  }

  let isActualDateError;
  if (["actual_start_date", "actual_end_date"].includes(metric?.metric_id)) {
    const madElectionOn = cap["apply_mean_accounting_date_election"];
    editable = !!madElectionOn;

    if (metric?.metric_id === "actual_start_date") {
      isActualDateError = isActualStartDateEntryInvalid;
    }

    if (metric?.metric_id === "actual_end_date") {
      isActualDateError = isActualEndDateEntryInvalid;
    }
  }

  let readonly = false;

  switch (metric?.type) {
    case "date":
      value = displayValue = value
        ? processDateJS(value)?.format("DD/MM/YYYY")
        : "";
      break;

    case "number":
      cellType = GridCellKind?.Number;
      if (isNaN(value) || !value) {
        value = 0;
      }
      displayValue = value?.toLocaleString();
      if (metric?.metric_id === "ct_rate_override") {
        if (value < 0) {
          value = 0;
          displayValue = value;
        }
        if (value > 100) {
          value = 100;
          displayValue = value;
        }
        displayValue = displayValue + "%";
      }
      break;

    case "calculation":
      cellType = GridCellKind.Number;
      value = metric?.calculation(cap);
      displayValue = value?.toLocaleString();
      editable = false;
      break;

    case "boolean":
      cellType = GridCellKind?.Boolean;
      value = ensureBoolean(value);
      displayValue = String?.valueOf(value);
      if (metric?.metric_id === "onesource_sync") {
        value = ensureBoolean(value && company?.from_onesource);
        displayValue = String?.valueOf(value && company?.from_onesource);
        editable = company?.from_onesource;
        readonly = !editable;
      }
      break;
  }

  if (metric?.metric_id === "fx_rate") {
    editable = cap["currency"] === "GBP" ? false : true;
  }

  // Do not allow to edit data cells if onesource_sync is true (unless it's the onesource_sync itself - you should be able to edit this whenever)
  let edit_data_lock =
    metric?.metric_id !== "onesource_sync" &&
    cap["onesource_sync"] &&
    metric?.onesourcedredged === true;

  // Check if the cap was used in previous periods, disable any editing on this page
  if (isCapUsedInPriorPoas) {
    edit_data_lock = true;
  }

  const read_only = metric?.hasOwnProperty("read_only")
    ? metric?.read_only(cap)
    : false;
  const isChangedMetric = !!changedMetricsForCap?.[metric?.metric_id];
  const isNetDtrPositive =
    metric?.metric_id === "net_dtr_adjustment_gbp" && value > 0;
  const isEditable = !edit_data_lock && editable;

  metric.editable = editable;
  return {
    kind: cellType,
    data: value,
    allowOverlay: !edit_data_lock && editable,
    displayData: displayValue,
    readonly: edit_data_lock || readonly || !editable,

    themeOverride: {
      ...cellTextStyle[metric?.style],
      bgCell: isChangedMetric
        ? isEditable
          ? "#ff66669a"
          : "#FF6666"
        : isEditable
          ? isNetDtrPositive
            ? "#FFCCCC"
            : isActualDateError
              ? "#FF6666"
              : "#FFFFFF"
          : isNetDtrPositive
            ? "#FFCCCC"
            : "#ffd5b7",
    },
  };
};

export const capDatesMatchPoADates = (cap, selectedPeriod) => {
  const { period_start, period_end } = selectedPeriod;

  const cap_start_date = cap?.start_date;
  const cap_end_date = cap?.end_date;

  const startDatesMatch = processDateJS(cap_start_date)?.isSame(
    processDateJS(period_start),
  );
  const endDatesMatch = processDateJS(cap_end_date)?.isSame(
    processDateJS(period_end),
  );

  return startDatesMatch && endDatesMatch;
};

export const capDatesWithinRange = (cap, actualStartDate, actualEndDate) => {
  const cap_start_date = cap?.start_date;
  const cap_end_date = cap?.end_date;

  const startDiff = getDaysDifference(cap_start_date, actualStartDate);
  const endDiff = getDaysDifference(cap_end_date, actualEndDate);

  return startDiff <= 4 && endDiff <= 4;
};
